import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Calendar, TrendingUp, Filter, RefreshCw } from 'lucide-react';
import axios from 'axios';

const SubmissionAnalytics = () => {
  const [analytics, setAnalytics] = useState({
    loading: true,
    error: null,
    weeklyData: [],
    monthlyData: []
  });

  const [dateFilter, setDateFilter] = useState({
    preset: 'last_3_months',
    startDate: '',
    endDate: '',
    showCustom: false
  });

  // Date preset options
  const datePresets = [
    { value: 'last_3_months', label: 'Last 3 Months' },
    { value: 'last_6_months', label: 'Last 6 Months' },
    { value: 'last_year', label: 'Last Year' },
    { value: 'all_time', label: 'All Time' },
    { value: 'custom', label: 'Custom Range' }
  ];

  useEffect(() => {
    loadAnalyticsData();
  }, [dateFilter.preset, dateFilter.startDate, dateFilter.endDate]);

  const loadAnalyticsData = async () => {
    setAnalytics(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const dateParams = getDateParams();
      
      const [weeklyResponse, monthlyResponse] = await Promise.all([
        axios.get('/api/submissions/analytics/weekly', { params: dateParams }),
        axios.get('/api/submissions/analytics/monthly', { params: dateParams })
      ]);
      
      // Format data for Recharts
      const weeklyData = formatWeeklyData(weeklyResponse.data.data);
      const monthlyData = formatMonthlyData(monthlyResponse.data.data);
      
      setAnalytics({
        loading: false,
        error: null,
        weeklyData,
        monthlyData
      });
      
    } catch (error) {
      console.error('Analytics loading error:', error);
      setAnalytics(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.error || 'Failed to load analytics'
      }));
    }
  };

  const getDateParams = () => {
    const now = new Date();
    let params = {};
    
    switch (dateFilter.preset) {
      case 'last_3_months':
        params.startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        params.endDate = now.toISOString().split('T')[0];
        break;
      case 'last_6_months':
        params.startDate = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        params.endDate = now.toISOString().split('T')[0];
        break;
      case 'last_year':
        params.startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        params.endDate = now.toISOString().split('T')[0];
        break;
      case 'custom':
        if (dateFilter.startDate && dateFilter.endDate) {
          params.startDate = dateFilter.startDate;
          params.endDate = dateFilter.endDate;
        }
        break;
      case 'all_time':
      default:
        // No date filters for all time
        break;
    }
    
    return params;
  };

  const formatWeeklyData = (data) => {
    return data.map(item => ({
      period: item.period,
      count: item.count,
      label: formatWeekLabel(item.period),
      fullDate: item.startDate
    }));
  };

  const formatMonthlyData = (data) => {
    return data.map(item => ({
      period: item.period,
      count: item.count,
      label: formatMonthLabel(item.period),
      fullDate: item.startDate
    }));
  };

  const formatWeekLabel = (period) => {
    // Format "2024-W01" to "Week 1, 2024"
    const [year, week] = period.split('-W');
    return `Week ${parseInt(week)}, ${year}`;
  };

  const formatMonthLabel = (period) => {
    // Format "2024-01" to "Jan 2024"
    const [year, month] = period.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const handlePresetChange = (preset) => {
    setDateFilter(prev => ({
      ...prev,
      preset,
      showCustom: preset === 'custom'
    }));
  };

  const handleCustomDateChange = (field, value) => {
    setDateFilter(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm text-gray-600 mb-1">{label}</p>
          <p className="text-sm font-semibold text-blue-600">
            {`Submissions: ${payload[0].value}`}
          </p>
        </div>
      );
    }
    return null;
  };

  if (analytics.loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
            <p className="text-gray-600">Loading analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Date Filter Controls */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Filter className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Analytics Filters</h3>
          </div>
          <button
            onClick={loadAnalyticsData}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>

        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Time Period
            </label>
            <select
              value={dateFilter.preset}
              onChange={(e) => handlePresetChange(e.target.value)}
              className="mt-1 block w-48 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              {datePresets.map(preset => (
                <option key={preset.value} value={preset.value}>
                  {preset.label}
                </option>
              ))}
            </select>
          </div>

          {dateFilter.showCustom && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={dateFilter.startDate}
                  onChange={(e) => handleCustomDateChange('startDate', e.target.value)}
                  className="mt-1 block w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={dateFilter.endDate}
                  onChange={(e) => handleCustomDateChange('endDate', e.target.value)}
                  className="mt-1 block w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Error Message */}
      {analytics.error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-800">{analytics.error}</p>
              <button
                onClick={loadAnalyticsData}
                className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Weekly Submissions Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <TrendingUp className="h-5 w-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Weekly Submissions</h3>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={analytics.weeklyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="label" 
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#2563eb" 
                  strokeWidth={2}
                  dot={{ fill: '#2563eb', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#2563eb', strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Monthly Submissions Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <Calendar className="h-5 w-5 text-green-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Monthly Submissions</h3>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={analytics.monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="label" 
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#16a34a" 
                  strokeWidth={2}
                  dot={{ fill: '#16a34a', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#16a34a', strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingUp className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Weekly Total</p>
              <p className="text-xl font-bold text-gray-900">
                {analytics.weeklyData.reduce((sum, item) => sum + item.count, 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Calendar className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Monthly Total</p>
              <p className="text-xl font-bold text-gray-900">
                {analytics.monthlyData.reduce((sum, item) => sum + item.count, 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingUp className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Weekly Average</p>
              <p className="text-xl font-bold text-gray-900">
                {analytics.weeklyData.length > 0 
                  ? Math.round(analytics.weeklyData.reduce((sum, item) => sum + item.count, 0) / analytics.weeklyData.length * 10) / 10
                  : 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Calendar className="h-8 w-8 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Monthly Average</p>
              <p className="text-xl font-bold text-gray-900">
                {analytics.monthlyData.length > 0 
                  ? Math.round(analytics.monthlyData.reduce((sum, item) => sum + item.count, 0) / analytics.monthlyData.length * 10) / 10
                  : 0}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmissionAnalytics;
